import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  message,
  Alert,
  Popconfirm,
  Card,
  Row,
  Col,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  KeyOutlined,
  SafetyOutlined,
  StopOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import {
  listUsers,
  createUser,
  updateUser,
  updateUserStatus,
  resetUserPassword,
  deleteUser,
  User,
  UserRole,
  UserStatus,
  CreateUserRequest,
  UpdateUserRequest,
} from '../../services/user';
import UserAuthorizationModal from './UserAuthorizationModal';
import apiError from '../../utils/apiError';

const { Option } = Select;
const { Search } = Input;

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [searchPhone, setSearchPhone] = useState('');
  const [filterStatus, setFilterStatus] = useState<number>();

  // 模态框状态
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [passwordModalVisible, setPasswordModalVisible] = useState(false);
  const [authModalVisible, setAuthModalVisible] = useState(false);
  const [currentUser, setCurrentUser] = useState<User | null>(null);

  const [createForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const [createError, setCreateError] = useState('');
  const [editError, setEditError] = useState('');
  const [passwordError, setPasswordError] = useState('');

  // 加载用户列表（只显示普通用户，不显示管理员）
  const loadUsers = async () => {
    setLoading(true);
    try {
      const res = await listUsers({
        phone: searchPhone || undefined,
        role: UserRole.USER, // 只查询普通用户
        status: filterStatus,
        page,
        pageSize: pageSize,
      });
      const data = res.data || res;
      setUsers(data.users || []);
      setTotal(data.total);
    } catch (error: any) {
      // 全局 Alert 已自动显示错误
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, [page, pageSize, searchPhone, filterStatus]);

  // 创建用户（默认创建普通用户）
  const handleCreate = async (values: CreateUserRequest) => {
    try {
      await createUser({
        ...values,
        role: UserRole.USER, // 强制设置为普通用户
      });
      message.success('创建用户成功');
      setCreateModalVisible(false);
      createForm.resetFields();
      loadUsers();
    } catch (error: any) {
      setCreateError(error.message || '创建用户失败');
      apiError.clear();
    }
  };

  // 更新用户
  const handleUpdate = async (values: UpdateUserRequest) => {
    if (!currentUser) return;
    try {
      await updateUser(currentUser.id, values);
      message.success('更新用户成功');
      setEditModalVisible(false);
      editForm.resetFields();
      loadUsers();
    } catch (error: any) {
      setEditError(error.message || '更新用户失败');
      apiError.clear();
    }
  };

  // 重置密码
  const handleResetPassword = async (values: { password: string }) => {
    if (!currentUser) return;
    try {
      await resetUserPassword(currentUser.id, values.password);
      message.success('重置密码成功');
      setPasswordModalVisible(false);
      passwordForm.resetFields();
    } catch (error: any) {
      setPasswordError(error.message || '重置密码失败');
      apiError.clear();
    }
  };

  // 更新状态
  const handleToggleStatus = async (user: User) => {
    const newStatus = user.status === UserStatus.ENABLED ? UserStatus.DISABLED : UserStatus.ENABLED;
    try {
      await updateUserStatus(user.id, newStatus);
      message.success(`${newStatus === UserStatus.ENABLED ? '启用' : '禁用'}用户成功`);
      loadUsers();
    } catch (error: any) {
      // 全局 Alert 已自动显示错误
    }
  };

  // 删除用户
  const handleDelete = async (user: User) => {
    try {
      await deleteUser(user.id);
      message.success('删除用户成功');
      loadUsers();
    } catch (error: any) {
      // 全局 Alert 已自动显示错误
    }
  };

  // 打开编辑模态框
  const showEditModal = (user: User) => {
    setCurrentUser(user);
    setEditError('');
    editForm.setFieldsValue({
      name: user.name,
      company: user.company,
      address: user.address,
      remark: user.remark,
    });
    setEditModalVisible(true);
  };

  // 打开重置密码模态框
  const showPasswordModal = (user: User) => {
    setCurrentUser(user);
    setPasswordError('');
    setPasswordModalVisible(true);
  };

  // 打开授权模态框
  const showAuthModal = (user: User) => {
    setCurrentUser(user);
    setAuthModalVisible(true);
  };

  // 表格列定义
  const columns: ColumnsType<User> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '手机号',
      dataIndex: 'phone',
      key: 'phone',
      width: 140,
    },
    {
      title: '姓名',
      dataIndex: 'name',
      key: 'name',
      width: 100,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: UserStatus) => (
        <Tag color={status === UserStatus.ENABLED ? 'green' : 'default'}>
          {status === UserStatus.ENABLED ? '启用' : '禁用'}
        </Tag>
      ),
    },
    {
      title: '单位',
      dataIndex: 'company',
      key: 'company',
      ellipsis: true,
    },
    {
      title: '最后登录时间',
      dataIndex: 'lastLoginAt',
      key: 'lastLoginAt',
      width: 180,
      render: (time: string) => (time ? new Date(time).toLocaleString('zh-CN') : '-'),
    },
    {
      title: '操作',
      key: 'action',
      width: 320,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<SafetyOutlined />}
            onClick={() => showAuthModal(record)}
          >
            授权
          </Button>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => showEditModal(record)}
          >
            编辑
          </Button>
          <Button
            type="link"
            size="small"
            icon={<KeyOutlined />}
            onClick={() => showPasswordModal(record)}
          >
            重置密码
          </Button>
          <Button
            type="link"
            size="small"
            icon={record.status === UserStatus.ENABLED ? <StopOutlined /> : <CheckCircleOutlined />}
            onClick={() => handleToggleStatus(record)}
          >
            {record.status === UserStatus.ENABLED ? '禁用' : '启用'}
          </Button>
          <Popconfirm
            title="确定要删除该用户吗？"
            description="删除后将无法恢复，且该用户的所有授权也会被删除。"
            onConfirm={() => handleDelete(record)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col>
            <Search
              placeholder="搜索手机号"
              allowClear
              style={{ width: 200 }}
              onSearch={(value) => {
                setSearchPhone(value);
                setPage(1);
              }}
            />
          </Col>
          <Col>
            <Select
              placeholder="筛选状态"
              allowClear
              style={{ width: 120 }}
              onChange={(value) => {
                setFilterStatus(value);
                setPage(1);
              }}
            >
              <Option value={UserStatus.ENABLED}>启用</Option>
              <Option value={UserStatus.DISABLED}>禁用</Option>
            </Select>
          </Col>
          <Col flex="auto" style={{ textAlign: 'right' }}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setCreateModalVisible(true);
                setCreateError('');
              }}
            >
              新建用户
            </Button>
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={users}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1200 }}
          pagination={{
            current: page,
            pageSize: pageSize,
            total: total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条`,
            onChange: (page, pageSize) => {
              setPage(page);
              setPageSize(pageSize);
            },
          }}
        />
      </Card>

      {/* 创建用户模态框 */}
      <Modal
        title="新建用户"
        open={createModalVisible}
        onOk={() => createForm.submit()}
        onCancel={() => {
          setCreateModalVisible(false);
          createForm.resetFields();
          setCreateError('');
        }}
        width={600}
      >
        <div style={{ minHeight: 42, marginBottom: 16 }}>
          {createError && (
            <Alert message={createError} type="error" showIcon closable onClose={() => setCreateError('')} />
          )}
        </div>
        <Form form={createForm} layout="vertical" onFinish={handleCreate}>
          <Form.Item
            label="手机号"
            name="phone"
            rules={[
              { required: true, message: '请输入手机号' },
              { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号' },
            ]}
          >
            <Input placeholder="请输入11位手机号" maxLength={11} />
          </Form.Item>
          <Form.Item
            label="初始密码"
            name="password"
            rules={[
              { required: true, message: '请输入密码' },
              { min: 6, message: '密码至少6位' },
            ]}
          >
            <Input.Password placeholder="请输入密码（至少6位）" />
          </Form.Item>
          <Form.Item label="姓名" name="name">
            <Input placeholder="请输入姓名" />
          </Form.Item>
          <Form.Item label="单位" name="company">
            <Input placeholder="请输入单位" />
          </Form.Item>
          <Form.Item label="地址" name="address">
            <Input placeholder="请输入地址" />
          </Form.Item>
          <Form.Item label="备注" name="remark">
            <Input.TextArea rows={3} placeholder="请输入备注" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 编辑用户模态框 */}
      <Modal
        title="编辑用户"
        open={editModalVisible}
        onOk={() => editForm.submit()}
        onCancel={() => {
          setEditModalVisible(false);
          editForm.resetFields();
          setEditError('');
        }}
        width={600}
      >
        <div style={{ minHeight: 42, marginBottom: 16 }}>
          {editError && (
            <Alert message={editError} type="error" showIcon closable onClose={() => setEditError('')} />
          )}
        </div>
        <Form form={editForm} layout="vertical" onFinish={handleUpdate}>
          <Form.Item label="姓名" name="name">
            <Input placeholder="请输入姓名" />
          </Form.Item>
          <Form.Item label="单位" name="company">
            <Input placeholder="请输入单位" />
          </Form.Item>
          <Form.Item label="地址" name="address">
            <Input placeholder="请输入地址" />
          </Form.Item>
          <Form.Item label="备注" name="remark">
            <Input.TextArea rows={3} placeholder="请输入备注" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 重置密码模态框 */}
      <Modal
        title="重置密码"
        open={passwordModalVisible}
        onOk={() => passwordForm.submit()}
        onCancel={() => {
          setPasswordModalVisible(false);
          passwordForm.resetFields();
          setPasswordError('');
        }}
      >
        <div style={{ minHeight: 42, marginBottom: 16 }}>
          {passwordError && (
            <Alert message={passwordError} type="error" showIcon closable onClose={() => setPasswordError('')} />
          )}
        </div>
        <Form form={passwordForm} layout="vertical" onFinish={handleResetPassword}>
          <Form.Item
            label="新密码"
            name="password"
            rules={[
              { required: true, message: '请输入新密码' },
              { min: 6, message: '密码至少6位' },
            ]}
          >
            <Input.Password placeholder="请输入新密码（至少6位）" />
          </Form.Item>
          <Form.Item
            label="确认密码"
            name="confirmPassword"
            dependencies={['password']}
            rules={[
              { required: true, message: '请确认密码' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('两次输入的密码不一致'));
                },
              }),
            ]}
          >
            <Input.Password placeholder="请再次输入密码" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 设备授权模态框 */}
      {currentUser && (
        <UserAuthorizationModal
          visible={authModalVisible}
          user={currentUser}
          onClose={() => {
            setAuthModalVisible(false);
            setCurrentUser(null);
          }}
        />
      )}
    </div>
  );
};

export default UserManagement;
